<template>
  <div>
    <spinner v-if="loading"></spinner>
    <p v-if="error" class="text-sm text-danger">
      {{ errorMessage }}
    </p>
    <template v-else>
      <div v-for="(chart, index) in data.charts" :key="index" class="relative">
        <div v-if="showCollapsedView(index)" class="metrics-chart-collapsed p-y-sm p-x-m" :class="{ 'cursor-pointer': defaultCollapsed }" :role="defaultCollapsed ? 'button' : null" :tabindex="defaultCollapsed ? 0 : null" :aria-expanded="defaultCollapsed ? 'false' : null" @click="defaultCollapsed ? toggle(index) : null" @keydown.enter.prevent="defaultCollapsed ? toggle(index) : null" @keydown.space.prevent="defaultCollapsed ? toggle(index) : null">
          <div class="flex align-items-center justify-space-between gap-xs m-b-xxs">
            <div class="flex-1 text-12 text-gray-700 metrics-chart-title" v-html="chart.payload.title.text"></div>
            <div class="flex align-items-center gap-xs flex-shrink-0">
              <span v-if="toolTipFor(chart)" class="print-hide" @click.stop>
                <VDropdown theme="info-tooltip">
                  <i class="fa-solid fa-circle-question text-gray-400 text-12"></i>
                  <template v-slot:popper>
                    <div>
                      <p class="m-n p-n" v-text="toolTipFor(chart)"></p>
                    </div>
                  </template>
                </VDropdown>
              </span>
              <span v-if="defaultCollapsed" class="text-12 text-gray-400 print-hide" aria-hidden="true">
                <i class="fa-solid fa-chevron-down"></i>
              </span>
            </div>
          </div>
          <div v-if="chart.payload.subtitle" v-html="chart.payload.subtitle.text"></div>
          <slot name="collapsed-extras" :chart="chart" :metrics="data.metrics"></slot>
        </div>
        <div v-else class="metrics-chart-expanded" :class="defaultCollapsed ? 'p-x-m p-y-sm' : ''">
          <div v-if="defaultCollapsed" class="flex align-items-center justify-space-between gap-xs m-b-xs">
            <button
              type="button"
              class="btn-link p-n cursor-pointer print-hide flex align-items-center gap-xxs"
              aria-label="Collapse chart"
              @click="toggle(index)"
            >
              <i class="fa-solid fa-chevron-up text-gray-500 text-12"></i>
              <span class="text-12 text-gray-600">Collapse</span>
            </button>
            <span v-if="toolTipFor(chart)" class="print-hide">
              <VDropdown theme="info-tooltip">
                <i class="fa-solid fa-circle-question text-gray-400 text-12"></i>
                <template v-slot:popper>
                  <div>
                    <p class="m-n p-n" v-text="toolTipFor(chart)"></p>
                  </div>
                </template>
              </VDropdown>
            </span>
          </div>
          <div v-else-if="toolTipFor(chart)" class="absolute print-hide" style="top: 8px; right: 8px; z-index: 1;">
            <VDropdown theme="info-tooltip">
              <i class="fa-solid fa-circle-question text-gray-400 text-12"></i>
              <template v-slot:popper>
                <div>
                  <p class="m-n p-n" v-text="toolTipFor(chart)"></p>
                </div>
              </template>
            </VDropdown>
          </div>
          <highchart-wrapper :use-exporting="false" :payload="chart.payload" :is-currency="isCurrency(chart)" :container-id="`chart-${index}-${chartKey}`"></highchart-wrapper>
          <slot name="expanded-extras" :chart="chart" :metrics="data.metrics"></slot>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import HighchartWrapper from './reports/highchart_wrapper.vue';
import { errorableMix } from './mixins/errorable_mix';
import Spinner from './spinner.vue';

export default {
  name: 'metrics-chart',
  props: {
    endpoint: {
      type: String,
      required: true,
    },
    chartKey: {
      type: String,
      required: true,
    },
    defaultCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [errorableMix],
  components: { HighchartWrapper, Spinner },
  data() {
    return {
      data: {
        metrics: [],
        charts: [],
      },
      loading: true,
      expandedByIndex: {},
    };
  },
  mounted() {
    this.loading = true;
    axios.get(this.endpoint)
      .then((response) => {
        this.data = response.data;
        const initial = !this.defaultCollapsed;
        this.expandedByIndex = (this.data.charts || []).reduce((acc, _, i) => {
          acc[i] = initial;
          return acc;
        }, {});
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        this.errorMessage = this.parseErrorResponse(error);
        this.error = true;
      });
  },
  methods: {
    isCurrency(chart) {
      return chart.metadata.is_currency === true;
    },
    toolTipFor(chart) {
      if (chart.metadata?.tooltip) {
        return chart.metadata.tooltip;
      }

      return null;
    },
    showCollapsedView(index) {
      if (!this.defaultCollapsed) return false;
      return !this.expandedByIndex[index];
    },
    toggle(index) {
      this.$set(this.expandedByIndex, index, !this.expandedByIndex[index]);
    },
  },
};
</script>
